import { useEffect, useRef, useState } from 'react';
import { Modal, Button } from '../primitives';
import styles from './MapPickerButton.module.css';

export interface MapPickerButtonProps {
  /** Aktuální adresa (předvyplní se do pole vyhledávání). */
  value: string;
  /** Callback po stisku „Propsat do dokumentu“. */
  onApply: (address: string) => void;
  /** Text tlačítka – standardně „📍 Mapa“. */
  label?: React.ReactNode;
  /** Nadpis dialogu. */
  title?: string;
  /** Pokud true, vykreslí pouze ikonku bez textu. */
  compact?: boolean;
  className?: string;
}

interface NominatimHit {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  type?: string;
  class?: string;
}

interface MapHandle {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  L: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  map: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  marker: any;
}

const LEAFLET_VERSION = '1.9.4';
const LEAFLET_CSS = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.css`;
const LEAFLET_JS = `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.js`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let leafletPromise: Promise<any> | null = null;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function loadLeaflet(): Promise<any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  if (w.L) return Promise.resolve(w.L);
  if (leafletPromise) return leafletPromise;
  leafletPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[data-leaflet="1"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      link.dataset.leaflet = '1';
      document.head.appendChild(link);
    }
    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => resolve(w.L);
    script.onerror = () => reject(new Error('Leaflet se nepodařilo načíst.'));
    document.head.appendChild(script);
  });
  return leafletPromise;
}

const PRAGUE: [number, number] = [50.0755, 14.4378];

/**
 * Otevře modální okno s interaktivní mapou (Leaflet + OSM). Uživatel může:
 *  - napsat adresu do pole → seznam nálezů z Nominatim, klik propíše adresu
 *  - kliknout přímo na mapu → adresa se načte reverzním geokódováním
 *  - text ručně doladit a potvrdit „✓ Propsat do dokumentu“.
 */
export function MapPickerButton({
  value,
  onApply,
  label,
  title = 'Najít místo plnění',
  compact,
  className,
}: MapPickerButtonProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(value);
  const [results, setResults] = useState<NominatimHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [reverseLoading, setReverseLoading] = useState(false);

  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<MapHandle | null>(null);
  const debounceRef = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const reverseAbortRef = useRef<AbortController | null>(null);

  function handleOpen() {
    setSearch(value);
    setResults([]);
    setError(null);
    setHasSearched(false);
    setOpen(true);
  }

  function handleClose() {
    setOpen(false);
    if (abortRef.current) abortRef.current.abort();
    if (reverseAbortRef.current) reverseAbortRef.current.abort();
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
  }

  async function reverseGeocode(lat: number, lng: number) {
    if (reverseAbortRef.current) reverseAbortRef.current.abort();
    const ctrl = new AbortController();
    reverseAbortRef.current = ctrl;
    setReverseLoading(true);
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?format=json&addressdetails=1&accept-language=cs&lat=${lat}&lon=${lng}`;
      const r = await fetch(url, { signal: ctrl.signal });
      if (!r.ok) throw new Error('bad status');
      const data = await r.json();
      if (data?.display_name) setSearch(data.display_name);
    } catch (err) {
      if ((err as DOMException)?.name === 'AbortError') return;
      // Neblokujeme — uživatel stále může adresu dopsat ručně.
    } finally {
      setReverseLoading(false);
    }
  }

  function placeMarker(lat: number, lng: number, zoom = 16) {
    const h = handleRef.current;
    if (!h) return;
    const { L, map } = h;
    if (h.marker) {
      h.marker.setLatLng([lat, lng]);
    } else {
      h.marker = L.marker([lat, lng], { draggable: true }).addTo(map);
      h.marker.on('dragend', () => {
        const ll = h.marker.getLatLng();
        void reverseGeocode(ll.lat, ll.lng);
      });
    }
    map.setView([lat, lng], zoom);
  }

  // Init Leaflet map when modal opens.
  useEffect(() => {
    if (!open) return;
    let disposed = false;
    setMapReady(false);

    loadLeaflet()
      .then(async (L) => {
        if (disposed) return;
        const el = mapDivRef.current;
        if (!el) return;

        // Počáteční střed: pokus o geokódování existující hodnoty, jinak Praha.
        let center: [number, number] = PRAGUE;
        let initialZoom = 6;
        const q = (value || '').trim();
        if (q) {
          try {
            const url =
              'https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=cz&accept-language=cs&q=' +
              encodeURIComponent(q);
            const r = await fetch(url);
            const hits: NominatimHit[] = await r.json();
            if (hits[0]) {
              center = [parseFloat(hits[0].lat), parseFloat(hits[0].lon)];
              initialZoom = 16;
            }
          } catch {
            /* ignore */
          }
        }
        if (disposed) return;

        const map = L.map(el).setView(center, initialZoom);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '© OpenStreetMap',
        }).addTo(map);

        const handle: MapHandle = { L, map, marker: null };
        handleRef.current = handle;

        if (initialZoom >= 13) {
          handle.marker = L.marker(center, { draggable: true }).addTo(map);
          handle.marker.on('dragend', () => {
            const ll = handle.marker.getLatLng();
            void reverseGeocode(ll.lat, ll.lng);
          });
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        map.on('click', (e: any) => {
          const { lat, lng } = e.latlng;
          placeMarker(lat, lng, map.getZoom());
          void reverseGeocode(lat, lng);
        });

        // Fix pro Leaflet v modálu — přepočet velikosti po otevření.
        setTimeout(() => map.invalidateSize(), 50);
        setMapReady(true);
      })
      .catch((e: Error) => {
        setError(e.message);
      });

    return () => {
      disposed = true;
      const h = handleRef.current;
      if (h) {
        h.map.remove();
        handleRef.current = null;
      }
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  async function runSearch(q: string) {
    const query = q.trim();
    if (!query) {
      setResults([]);
      setError(null);
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const url =
        'https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=6&accept-language=cs&countrycodes=cz&q=' +
        encodeURIComponent(query);
      const r = await fetch(url, { signal: ctrl.signal });
      if (!r.ok) throw new Error('bad status');
      const data: NominatimHit[] = await r.json();
      setResults(data);
      setHasSearched(true);
    } catch (err) {
      if ((err as DOMException)?.name === 'AbortError') return;
      setResults([]);
      setError('Hledání se nepodařilo. Zkuste to znovu.');
    } finally {
      setLoading(false);
    }
  }

  // Debounced live search while typing.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    const q = search.trim();
    if (q.length < 3) {
      setResults([]);
      return;
    }
    debounceRef.current = window.setTimeout(() => {
      void runSearch(q);
    }, 400);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [search, open]);

  function handleSearchClick() {
    void runSearch(search);
  }

  function handlePick(hit: NominatimHit) {
    setSearch(hit.display_name);
    setResults([]);
    const lat = parseFloat(hit.lat);
    const lng = parseFloat(hit.lon);
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      placeMarker(lat, lng, 17);
    }
  }

  function handleApply() {
    const q = search.trim();
    if (!q) return;
    onApply(q);
    setOpen(false);
  }

  return (
    <>
      <button
        type="button"
        className={`${styles.trigger}${className ? ' ' + className : ''}`}
        onClick={handleOpen}
        title="Vybrat adresu z mapy"
      >
        <span aria-hidden>📍</span>
        {!compact && <span>{label ?? 'Mapa'}</span>}
      </button>

      <Modal
        open={open}
        onClose={handleClose}
        title={title}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={handleClose}>
              Zrušit
            </Button>
            <Button variant="primary" onClick={handleApply} disabled={!search.trim()}>
              ✓ Propsat do dokumentu
            </Button>
          </>
        }
      >
        <div className={styles.body}>
          <div className={styles.searchRow}>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearchClick();
                }
              }}
              placeholder="Zadejte adresu, obec, p.č., k.ú.…"
              className={styles.searchInput}
              autoFocus
            />
            <Button variant="secondary" onClick={handleSearchClick} disabled={loading}>
              {loading ? 'Hledám…' : 'Hledat'}
            </Button>
          </div>

          {(results.length > 0 || error || (hasSearched && !loading)) && (
            <div className={styles.suggestions}>
              {error && <div className={styles.suggestEmpty}>{error}</div>}
              {!error && results.length === 0 && hasSearched && !loading && (
                <div className={styles.suggestEmpty}>Žádné výsledky.</div>
              )}
              {results.map((hit) => (
                <button
                  type="button"
                  key={hit.place_id}
                  className={styles.suggestItem}
                  onClick={() => handlePick(hit)}
                  title="Použít tuto adresu"
                >
                  <span className={styles.suggestPin} aria-hidden>📍</span>
                  <span className={styles.suggestText}>{hit.display_name}</span>
                </button>
              ))}
            </div>
          )}

          <div className={styles.mapWrap}>
            <div ref={mapDivRef} className={styles.mapEl} />
            {!mapReady && <div className={styles.mapLoading}>Načítám mapu…</div>}
          </div>
          <div className={styles.hint}>
            Klikněte do mapy nebo vyberte ze seznamu — adresa se načte sama.
            {reverseLoading && ' · Načítám adresu…'}
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Adresa k propsání…"
            className={styles.resultInput}
          />
        </div>
      </Modal>
    </>
  );
}
